import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table,
  Paper,
  Pagination,
  useTheme,
  Typography,
  TextField,
  LinearProgress,
  Backdrop,
  CircularProgress,
} from '@mui/material';

import RowTableEstadia from '../SubComponents/Estadias/RowTableEstadia';
import EmptyState from '../common/EmptyState';
import { blue, grey } from '@mui/material/colors';
import { useAuth } from '../../Auth/AuthContext';
import { getTopSearchMatchIds } from '../../utils/searchHighlights';

const API_URL = process.env.REACT_APP_API_URL;

function Estadias({ createdTarget, onCreatedTargetConsumed }) {
  const { user } = useAuth();
  const areUserAdmin = user.rol === 'Administrador';

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterEstado, setFilterEstado] = useState(""); // "", "adentro", "afuera"

  const rowsPerPage = 30;

  // ✅ CACHE
  const cacheRef = useRef({});
  const locatedHighlightRef = useRef(null);
  const highlightId = createdTarget?.targetSection === 'Estadias' ? createdTarget.createdId : null;

  useEffect(() => {
    if (!highlightId) return;
    cacheRef.current = {};
    locatedHighlightRef.current = null;
    setFilterEstado('');
    setSearch('');
    setSearchInput('');
    setCurrentPage(1);
  }, [highlightId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterEstado]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const key = `${search}|${filterEstado}|${currentPage}|${highlightId || ''}`;

      const cached = cacheRef.current[key];

      // ✅ SI HAY CACHE → usarlo instantáneo
      if (cached) {
        setData(cached.results);
        setCount(cached.count);
        setTotalPages(Math.ceil(cached.count / rowsPerPage));
        return;
      }

      setLoading(true);
      setLoadError(false);

      try {
        const res = await axios.get(`${API_URL}/estadias/listado/`, {
          params: {
            search,
            filter: filterEstado,
            page: currentPage,
            page_size: rowsPerPage,
            highlight_id: highlightId || undefined,
          },
        });

        if (!isMounted) return;

        const results = res.data.results || [];
        const total = res.data.count || 0;

        // ✅ GUARDAR EN CACHE
        cacheRef.current[key] = {
          results,
          count: total,
        };

        setData(results);
        setCount(total);
        setTotalPages(Math.ceil(total / rowsPerPage));

      } catch (error) {
        console.log("Error fetching estadias:", error);
        if (isMounted) setLoadError(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [currentPage, search, filterEstado, highlightId]);

  useEffect(() => {
    if (highlightId && !loading && !loadError && currentPage === 1 && !search && !filterEstado && !data.some((item) => Number(item.id) === Number(highlightId))) {
      console.warn(`No se encontró la estadía recién creada con ID ${highlightId}.`);
      onCreatedTargetConsumed?.(highlightId);
    }
  }, [currentPage, data, filterEstado, highlightId, loadError, loading, onCreatedTargetConsumed, search]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setSearch(searchInput);
      setCurrentPage(1);
    }
  };

  const handleUpdateData = useCallback((updatedEstadia) => {
    setData((prevData) =>
      prevData.map((estadia) =>
        estadia.id === updatedEstadia.id ? updatedEstadia : estadia
      )
    );
  }, []);
  const handleHighlightLocated = useCallback((id) => {
    locatedHighlightRef.current = Number(id);
  }, []);

  const searchHighlightIds = useMemo(() => new Set(getTopSearchMatchIds(
    data,
    search,
    [
      'id',
      (item) => item.historial?.placa,
      (item) => item.cliente?.nombre,
      (item) => item.cliente?.apellidos,
      (item) => item.cliente?.documento,
    ]
  )), [data, search]);

  // ✅ MEMO REAL (evita renders innecesarios)
  const rows = useMemo(() => {
    return data.map((item) => (
      <RowTableEstadia
        key={item.id}
        item={item}
        setData={setData}
        setLoading={setLoading}
        handleUpdateData={handleUpdateData}
        areUserAdmin={areUserAdmin}
        highlighted={Number(item.id) === Number(highlightId)}
        scrollHighlight={Number(item.id) === Number(highlightId) && locatedHighlightRef.current !== Number(highlightId)}
        onHighlightLocated={handleHighlightLocated}
        searchHighlighted={searchHighlightIds.has(item.id)}
        highlightSource={createdTarget?.source}
      />
    ));
  }, [data, handleHighlightLocated, handleUpdateData, areUserAdmin, createdTarget?.source, highlightId, searchHighlightIds]);

  return (
    <Box sx={{ paddingTop: 1 }}>

      <Typography variant='h5' color='grey' sx={{ paddingBottom: 2 }}>
        TOTAL DE ESTADIAS: {count}
      </Typography>

      {/* ❌ SOLO mostrar overlay si no hay datos */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* ✅ barra liviana si ya hay data */}
      {loading && data.length > 0 && <LinearProgress sx={{ mb: 1 }} />}

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label="Buscar por patente o cliente"
          value={searchInput}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          size="small"
        />
      </Box>

      <Box sx={{ display: "flex", flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box
          onClick={() => {
            setFilterEstado("");
            setCurrentPage(1);
          }}
          sx={{
            px: 2,
            py: 1,
            borderRadius: 2,
            cursor: "pointer",
            backgroundColor: filterEstado === "" ? "#1976d2" : "#8a8a8a",
            color: filterEstado === "" ? "white" : "black",
          }}
        >
          Todos
        </Box>

        <Box
          onClick={() => {
            setFilterEstado("adentro");
            setCurrentPage(1);
          }}
          sx={{
            px: 2,
            py: 1,
            borderRadius: 2,
            cursor: "pointer",
            backgroundColor: filterEstado === "adentro" ? "#2e7d32" : "#8a8a8a",
            color: filterEstado === "adentro" ? "white" : "black",
          }}
        >
          Adentro
        </Box>

        <Box
          onClick={() => {
            setFilterEstado("afuera");
            setCurrentPage(1);
          }}
          sx={{
            px: 2,
            py: 1,
            borderRadius: 2,
            cursor: "pointer",
            backgroundColor: filterEstado === "afuera" ? "#d32f2f" : "#8a8a8a",
            color: filterEstado === "afuera" ? "white" : "black",
          }}
        >
          Afuera
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead
            sx={{
              backgroundColor: isDarkMode ? grey[800] : blue[700],
            }}
          >
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ESTADO</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>VEHÍCULO</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>CLIENTE</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>FECHA-HORA LLEGADA</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>FECHA-HORA SALIDA</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>DURACIÓN</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>TARIFA</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>VALOR PAGADO</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>OBS ENTR</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>OBS SAL</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>USER ENTR</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>USER SAL</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>
                ACCIONES
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13}>
                  <EmptyState message={loadError ? 'No fue posible cargar las estadías.' : search ? 'No se encontraron resultados.' : 'No hay estadías para mostrar.'} />
                </TableCell>
              </TableRow>
            ) : rows}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

    </Box>
  );
}

export default Estadias;
